import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createBrowserRouter, createRoutesFromElements, RouterProvider, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { FraimeLumanityProvider } from "@/lib/lumanity";
import { DocumentViewerProvider } from "@/contexts/DocumentViewerContext";
import { DocumentViewerSheet } from "@/components/DocumentViewerSheet";
import ProtectedRoute, { AdminRoute } from "@/components/ProtectedRoute";
import Login from "./pages/Login";
import Index from "./pages/Index";
import CreateProject from "./pages/CreateProject";
import EditProject from "./pages/EditProject";
import ProjectDetail from "./pages/ProjectDetail";
import ProjectFiles from "./pages/ProjectFiles";
import EvidenceBase from "./pages/EvidenceBase";
import Admin from "./pages/Admin";
import AllProjects from "./pages/AllProjects";
import NotFound from "./pages/NotFound";
import TherapyAreas from "./pages/admin/TherapyAreas";
import ProjectCodes from "./pages/admin/ProjectCodes";
import EvidenceTypes from "./pages/admin/EvidenceTypes";
import SourceTypes from "./pages/admin/SourceTypes";
import SupportingDocumentTypes from "./pages/admin/SupportingDocumentTypes";
import UsersAdmin from "./pages/admin/Users";
import ProjectAvailability from "./pages/admin/ProjectAvailability";
import RegisteredApps from "./pages/admin/RegisteredApps";

const queryClient = new QueryClient();

const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<ProtectedRoute><Index /></ProtectedRoute>} />
      <Route path="/projects" element={<ProtectedRoute><AllProjects /></ProtectedRoute>} />
      <Route path="/projects/new" element={<ProtectedRoute><CreateProject /></ProtectedRoute>} />
      <Route path="/projects/:id" element={<ProtectedRoute><ProjectDetail /></ProtectedRoute>} />
      <Route path="/projects/:id/files" element={<ProtectedRoute><ProjectFiles /></ProtectedRoute>} />
      <Route path="/projects/:id/edit" element={<ProtectedRoute><EditProject /></ProtectedRoute>} />
      <Route path="/evidence" element={<ProtectedRoute><EvidenceBase /></ProtectedRoute>} />
      <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>}>
        <Route index element={<Navigate to="users" replace />} />
        <Route path="users" element={<UsersAdmin />} />
        <Route path="therapy-areas" element={<TherapyAreas />} />
        <Route path="project-codes" element={<ProjectCodes />} />
        <Route path="evidence-types" element={<EvidenceTypes />} />
        <Route path="source-types" element={<SourceTypes />} />
        <Route path="supporting-document-types" element={<SupportingDocumentTypes />} />
        <Route path="project-availability" element={<ProjectAvailability />} />
        <Route path="registered-apps" element={<RegisteredApps />} />
      </Route>
      <Route path="*" element={<NotFound />} />
    </>
  )
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <FraimeLumanityProvider queryClient={queryClient}>
        <DocumentViewerProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <DocumentViewerSheet />
            <RouterProvider router={router} />
          </TooltipProvider>
        </DocumentViewerProvider>
      </FraimeLumanityProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
